// src/Admin/serverTime.js
/* =========================================================
   Times saved by SQL Server with GETDATE() use the SERVER's own clock,
   which is US Pacific time, and the database driver sends them with a "Z"
   as if they were UTC. Example: a visit at 10:20 AM Beirut comes back as
   "…T00:20:00.000Z", which the browser showed as 3:20 AM.

   fromServer() reads the saved digits as US Pacific time to get the real
   moment; the helpers below then show it in Lebanon time.
   If the server ever moves, set REACT_APP_SERVER_TIMEZONE in .env.
   If the database is changed to save GETUTCDATE() instead, stop using
   fromServer() for those columns (use new Date(value)).
   ========================================================= */
export const SERVER_TZ = process.env.REACT_APP_SERVER_TIMEZONE || 'America/Los_Angeles';
export const DISPLAY_TZ = 'Asia/Beirut';

// minutes a time zone is ahead of UTC at a given moment (handles summer/winter time)
const tzOffsetMin = (date, tz) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date);
  const m = Object.fromEntries(parts.map((x) => [x.type, x.value]));
  const asUTC = Date.UTC(+m.year, +m.month - 1, +m.day, +m.hour, +m.minute, +m.second);
  return Math.round((asUTC - date.getTime()) / 60000);
};

export const fromServer = (value) => {
  if (!value) return null;
  const str = String(value);
  const w = str.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!w) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // the saved digits are server wall-clock time (a trailing "Z" is ignored on purpose)
  const wall = Date.UTC(+w[1], +w[2] - 1, +w[3], +w[4], +w[5], +(w[6] || 0));
  const off = tzOffsetMin(new Date(wall), SERVER_TZ);
  let t = wall - off * 60000;
  const off2 = tzOffsetMin(new Date(t), SERVER_TZ); // fix the hour around the summer/winter time change
  if (off2 !== off) t = wall - off2 * 60000;
  return new Date(t);
};

/* "Oct 8, 10:20 AM" in Lebanon time (adds the year when it isn't this year) */
export const fmtServer = (value) => {
  const d = fromServer(value);
  if (!d) return null;
  const y = (x) => new Intl.DateTimeFormat('en-US', { timeZone: DISPLAY_TZ, year: 'numeric' }).format(x);
  return d.toLocaleString('en-US', {
    timeZone: DISPLAY_TZ,
    month: 'short',
    day: 'numeric',
    year: y(d) === y(new Date()) ? undefined : 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/* "18 min ago", "3h ago", "2d ago" */
export const ago = (d) => {
  if (!d) return '';
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { timeZone: DISPLAY_TZ, month: 'short', day: 'numeric' });
};